// ============================================================================
// SHARED TYPES - frontend/src/types.ts
// TypeScript interfaces used across the application
// ============================================================================

export interface WorkshopPhase {
  id: string;
  name: string;
  duration: string;
  objectives: string;
  goal: string;
  successCriteria: string[];
}

export interface Avatar {
  name: string;
  emoji: string;
  color: string;
}

export interface ResponseMode {
  name: string;
  icon: string;
  color: string;
  description: string;
}

export interface TranscriptEntry {
  id?: string;
  speaker: 'human' | 'avatar' | 'whiteboard';
  avatarRole?: string;
  speakerId?: string;
  text: string;
  timestamp: string;
  audioUrl?: string;
  sourceId?: string;  // For whiteboard entries
  sourceName?: string;  // For whiteboard entries
  attributedTo?: string[];  // Attendee IDs from session attendees/facilitators

  // Absent = committed. Other values only appear on legacy rows from the removed
  // transcript staging pipeline (ADO #194).
  state?: 'staged' | 'held' | 'claiming' | 'noise' | 'committed';
}

export interface FinalizedSentence {
  id: string;
  text: string;
  confidence: number;
  timestamp: string;
  isNew: boolean;  // For fade-in animation
}

export interface WorkshopBackground {
  customer: string;
  industry: string;
  context: string;
}

export interface WorkshopAttendee {
  name: string;
  role: string;
}

export interface WhiteboardSource {
  sourceId: string;
  sourceName: string;
  lastUpload: string;
}

export const PHASES: WorkshopPhase[] = [
  {
    id: 'intros',
    name: 'Intros',
    duration: '10 min',
    objectives: 'Set context, introduce participants, establish goals',
    goal: 'Clearly articulate the problem or opportunity we\'re addressing and align on workshop goals',
    successCriteria: [
      'Problem statement is clearly articulated',
      'Workshop goals are explicitly stated',
      'Attendees understand why we\'re here',
      'Scope boundaries are defined'
    ]
  },
  {
    id: 'challenge_deep_dive',
    name: 'Challenge Deep Dive',
    duration: '30 min',
    objectives: 'Deeply understand the problem space',
    goal: 'Uncover root causes, validate assumptions, and understand the full scope of the problem',
    successCriteria: [
      'Root causes identified (not just symptoms)',
      'Key assumptions have been challenged',
      'User pain points are well understood',
      'Technical constraints are surfaced',
      'Data/research validates problem understanding'
    ]
  },
  {
    id: 'mini_prfaq',
    name: 'Mini PRFAQ',
    duration: '30 min',
    objectives: 'Articulate proposed solution',
    goal: 'Define what success looks like and how the solution will work (at a high level)',
    successCriteria: [
      'Vision for the solution is clear',
      'Key features/capabilities are defined',
      'Customer benefits are articulated',
      'Success metrics are identified',
      'Solution addresses validated problem'
    ]
  },
  {
    id: 'persona_identification',
    name: 'Persona Identification',
    duration: '20 min',
    objectives: 'Define 2-4 key user personas',
    goal: 'Create concrete, specific personas representing diverse user needs',
    successCriteria: [
      '2-4 distinct personas defined',
      'Each persona has clear goals and pain points',
      'Personas represent diverse user types',
      'Edge cases and vulnerable users considered',
      'Personas are based on real data/research where possible'
    ]
  },
  {
    id: 'user_journeys',
    name: 'User Journeys',
    duration: '40 min',
    objectives: 'Map user flows and interactions',
    goal: 'Understand step-by-step how users will interact with the solution',
    successCriteria: [
      'End-to-end journey mapped for key personas',
      'Touchpoints and interactions identified',
      'Pain points in journey are surfaced',
      'Accessibility considerations addressed',
      'Happy path and error cases explored'
    ]
  },
  {
    id: 'assumptions_constraints',
    name: 'Assumptions & Constraints',
    duration: '20 min',
    objectives: 'Surface critical assumptions',
    goal: 'Identify and validate assumptions that could derail success',
    successCriteria: [
      'Critical assumptions explicitly stated',
      'Technical constraints identified',
      'Risks and dependencies surfaced',
      'Compliance/ethical concerns addressed',
      'Validation plan for risky assumptions'
    ]
  },
  {
    id: 'next_steps',
    name: 'Next Steps',
    duration: '15 min',
    objectives: 'Define actionable follow-ups',
    goal: 'Leave with clear, prioritized next steps and owners',
    successCriteria: [
      'Specific next actions defined',
      'Owners assigned to each action',
      'Priorities are clear',
      'Timeline/deadlines established',
      'Follow-up meeting scheduled if needed'
    ]
  }
];

export const AVATARS: Record<string, Avatar> = {
  devils_advocate: { name: "Devil's Advocate", emoji: '🤔', color: '#ef4444' },
  tech_feasibility: { name: 'Tech Checker', emoji: '🔧', color: '#3b82f6' },
  customer_champion: { name: 'Customer Voice', emoji: '👤', color: '#10b981' },
  user_researcher: { name: 'User Researcher', emoji: '📊', color: '#06b6d4' },
  product_manager: { name: 'Product Manager', emoji: '📋', color: '#6366f1' },
  esg_advisor: { name: 'ESG Advisor', emoji: '🌱', color: '#059669' },
  responsible_ai: { name: 'Responsible AI', emoji: '🛡️', color: '#7c3aed' }
};

export const RESPONSE_MODES: Record<string, ResponseMode> = {
  open_ideas: {
    name: 'Open Ideas Mode',
    icon: '🌟',
    color: '#10b981',
    description: 'Encourage divergent thinking and exploration'
  },
  closing_down: {
    name: 'Closing Down',
    icon: '🎯',
    color: '#f59e0b',
    description: 'Converge to decisions and priorities'
  },
  creative_thinking: {
    name: 'Creative Thinking',
    icon: '🎨',
    color: '#ec4899',
    description: 'Wild ideas, cross-domain thinking'
  },
  critical_analysis: {
    name: 'Critical Analysis',
    icon: '🔍',
    color: '#ef4444',
    description: 'Deep scrutiny, identify risks'
  },
  consensus_building: {
    name: 'Consensus Building',
    icon: '🤝',
    color: '#8b5cf6',
    description: 'Find common ground, build alignment'
  },
  risk_assessment: {
    name: 'Risk Assessment',
    icon: '⚠️',
    color: '#dc2626',
    description: 'Focus on risks and worst-case scenarios'
  },
  next_steps: {
    name: 'Next Steps',
    icon: '➡️',
    color: '#3b82f6',
    description: 'Focus on actionable next steps and owners'
  },
  blank: {
    name: 'Blank',
    icon: '⬜',
    color: '#94a3b8',
    description: 'No avatar output'
  }
};

// ============================================================================
// AVATAR CATALOG TYPES
// ============================================================================

export enum AvatarScope {
  SESSION = 'session',
  USER = 'user',
  ORGANIZATION = 'organization',
  PROJECT = 'project'
}

export enum AvatarCategory {
  FACILITATOR = 'FACILITATOR',
  DOMAIN_EXPERT = 'DOMAIN_EXPERT',
  STAKEHOLDER = 'STAKEHOLDER',
  RESEARCHER = 'RESEARCHER',
  CUSTOM = 'CUSTOM'
}

export enum TTSProvider {
  AWS_POLLY = 'aws_polly',
  ELEVENLABS = 'elevenlabs',
  AZURE_SPEECH = 'azure_speech'
}

export enum AvatarBackendType {
  PROMPT = 'PROMPT',
  BEDROCK_AGENT = 'BEDROCK_AGENT',
  BEDROCK_KNOWLEDGE_BASE = 'BEDROCK_KNOWLEDGE_BASE'
}

export enum PublishingStatus {
  DRAFT = 'draft',
  PENDING_REVIEW = 'pending_review',
  APPROVED = 'approved',
  REJECTED = 'rejected'
}

export interface KnowledgeBaseConfig {
  retrievalMaxResults?: number;
  minRelevanceScore?: number;
}

// A single lens / mandatory question / evidence rule / decision criterion. `topics`
// gates when it activates in a conversation turn (empty = always active).
export interface CognitiveElement {
  id: string;
  text: string;
  topics: string[];
}

// The avatar's reasoning method, synced to Neo4j for topic-adaptive activation in the
// participant 1:1 chat path. Distinct from `systemPrompt` (identity/tone only).
export interface AvatarCognitivePolicy {
  goals: string[];
  lenses: CognitiveElement[];
  mandatoryQuestions: CognitiveElement[];
  evidenceRules: CognitiveElement[];
  decisionCriteria: CognitiveElement[];
}

export interface AvatarDefinition {
  avatarDefinitionId: string;
  versionNumber: number;
  name: string;
  description: string;

  // Scope & Ownership
  scope: AvatarScope;
  ownerId: string;
  createdBy: string;
  isPublic?: boolean;

  // Behavior Configuration
  category: AvatarCategory;
  systemPrompt: string;
  backendType: AvatarBackendType;
  cognitivePolicy?: AvatarCognitivePolicy;

  // Knowledge Base (RAG)
  knowledgeBaseId?: string;
  knowledgeBaseConfig?: KnowledgeBaseConfig;

  // Avatar Knowledge Base (Phase 7)
  kbConfig?: {
    enabled: boolean;
    useSharedCollection: boolean;
    aossCollectionId?: string;
    aossCollectionEndpoint?: string;
    autoQueryDuringSessions: boolean;
    queryTriggers?: string[];
  };

  // Metadata (for search/organization only - avatar selection is LLM-based)
  keywords: string[];  // Optional tags for search and categorization
  keywordsByPhase?: Record<string, string[]>;  // Deprecated: No longer used for triggering
  maxResponseLength: number;

  // Model Selection (Phase 7+)
  defaultModelId?: string;  // Bedrock model ID for this avatar (e.g., "eu.anthropic.claude-3-sonnet-20240229-v1:0")
                            // If not set, uses default from ai-services.yaml
                            // Can be overridden by cost-aware model selection

  // TTS Configuration
  ttsProvider: TTSProvider;
  ttsVoiceId?: string;  // Optional - not all avatars need TTS
  ttsConfig: Record<string, any>;

  // Avatar Image & Colors
  imageUrl?: string;
  primaryColor?: string;
  secondaryColor?: string;

  // Physical Device Support
  supportsPhysicalDevice: boolean;

  // Publishing & Lifecycle
  publishingStatus: PublishingStatus;
  status: 'ACTIVE' | 'DEPRECATED';
  isEnabled?: boolean; // false = temporarily disabled from sessions

  // Version Control
  previousVersionNumber?: number;
  changeNotes?: string;

  // Metadata
  createdAt: string;
  updatedAt: string;
  reviewedBy?: string;
  reviewedAt?: string;
  rejectionReason?: string;
}

export interface AvatarReviewQueueEntry {
  reviewId: string;
  avatarDefinitionId: string;
  versionNumber: number;
  submittedBy: string;
  submittedAt: string;
  organizationId: string;
  targetScope: 'organization';
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'WITHDRAWN';
  reviewedBy?: string;
  reviewedAt?: string;
  reviewNotes?: string;
  submissionNotes?: string;
}

export interface AvatarGroup {
  avatarGroupId: string;
  name: string;
  description: string;
  organizationId: string;
  avatarSlots: Array<{
    slotNumber: number;
    avatarDefinitionId: string;
    versionNumber: number;
  }>;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface BrowseAvatarsParams {
  scope?: AvatarScope;
  ownerId?: string;
  category?: AvatarCategory;
  isPublic?: boolean;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface CreateAvatarInput {
  name: string;
  description: string;
  systemPrompt: string;
  category?: AvatarCategory;
  scope?: AvatarScope;
  ownerId?: string;
  createdBy: string;
  backendType?: AvatarBackendType;
  knowledgeBaseId?: string;
  knowledgeBaseConfig?: KnowledgeBaseConfig;
  cognitivePolicy?: AvatarCognitivePolicy;
  keywords?: string[];
  maxResponseLength?: number;
  defaultModelId?: string;  // Bedrock model ID for this avatar
  ttsProvider?: TTSProvider;
  ttsVoiceId?: string;
  ttsConfig?: Record<string, any>;
  supportsPhysicalDevice?: boolean;
  isPublic?: boolean;
  imageUrl?: string;
  primaryColor?: string;
  secondaryColor?: string;
}

export interface UpdateAvatarInput {
  name?: string;
  description?: string;
  systemPrompt?: string;
  category?: AvatarCategory;
  cognitivePolicy?: AvatarCognitivePolicy;
  keywords?: string[];
  maxResponseLength?: number;
  defaultModelId?: string;  // Bedrock model ID for this avatar
  ttsProvider?: TTSProvider;
  ttsVoiceId?: string;
  ttsConfig?: Record<string, any>;
  supportsPhysicalDevice?: boolean;
  isPublic?: boolean;
  knowledgeBaseId?: string;
  knowledgeBaseConfig?: KnowledgeBaseConfig;
  kbConfig?: {
    enabled: boolean;
    useSharedCollection: boolean;
    aossCollectionId?: string;
    aossCollectionEndpoint?: string;
    autoQueryDuringSessions: boolean;
    queryTriggers?: string[];
  };
  imageUrl?: string;
  primaryColor?: string;
  secondaryColor?: string;
}

// ============================================================================
// SESSION TEMPLATE TYPES
// ============================================================================

export enum TemplateScope {
  SESSION = 'SESSION',
  USER = 'USER',
  ORGANIZATION = 'ORGANIZATION'
}

export enum TemplatePublishingStatus {
  DRAFT = 'DRAFT',
  PENDING_REVIEW = 'PENDING_REVIEW',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED'
}

export enum TemplateType {
  WORKSHOP = 'WORKSHOP',
  BRAINSTORM = 'BRAINSTORM',
  REVIEW = 'REVIEW',
  PLANNING = 'PLANNING',
  CUSTOM = 'CUSTOM'
}

export enum PhaseCategory {
  KICKOFF = 'KICKOFF',
  DISCOVERY = 'DISCOVERY',
  IDEATION = 'IDEATION',
  RESEARCH = 'RESEARCH',
  ANALYSIS = 'ANALYSIS',
  WRAP_UP = 'WRAP_UP',
  CUSTOM = 'CUSTOM'
}

export interface PhaseTemplate {
  phaseTemplateId: string;
  versionNumber: number;
  name: string;
  description: string;
  duration: string;
  purpose: string;
  goal: string;
  successCriteria: string[];
  category: PhaseCategory;
  scope: TemplateScope;
  ownerId: string;
  createdBy: string;
  isPrebuilt?: boolean;
  publishingStatus: TemplatePublishingStatus;
  status: 'ACTIVE' | 'DEPRECATED';
  previousVersionNumber?: number;
  changeNotes?: string;
  usageCount?: number;
  createdAt: string;
  updatedAt: string;
  reviewedBy?: string;
  reviewedAt?: string;
  rejectionReason?: string;
}

export interface PhaseReference {
  phaseTemplateId: string;
  versionNumber: number;
  orderIndex: number;
  customizations?: {
    duration?: string;
    successCriteria?: string[];
  };
}

export interface OutputDefinition {
  id: string;
  name: string;
  type: 'predefined' | 'custom';
  schema?: object;
  exampleBased?: {
    examples: string[];
    format: string;
  };
  promptBased?: {
    generationPrompt: string;
    validationPrompt?: string;
  };
  required: boolean;
}

export interface OutputDefinitions {
  outputs: OutputDefinition[];
}

export interface SessionTemplate {
  sessionTemplateId: string;
  versionNumber: number;
  name: string;
  description: string;
  purpose: string;
  goal: string;
  category: TemplateType;
  scope: TemplateScope;
  ownerId: string;
  createdBy: string;
  phaseReferences: PhaseReference[];
  outputDefinitions?: OutputDefinitions;

  isPrebuilt?: boolean;
  isDefault?: boolean;
  publishingStatus: TemplatePublishingStatus;
  status: 'ACTIVE' | 'DEPRECATED';
  previousVersionNumber?: number;
  changeNotes?: string;
  usageCount?: number;
  createdAt: string;
  updatedAt: string;
  reviewedBy?: string;
  reviewedAt?: string;
  rejectionReason?: string;
}

export interface TemplateSnapshot {
  sessionTemplateId: string;
  versionNumber: number;
  name: string;
  description: string;
  phases: Array<{
    phaseTemplateId: string;
    versionNumber: number;
    name: string;
    description: string;
    duration: string;
    purpose: string;
    goal: string;
    successCriteria: string[];
    category: PhaseCategory;
    orderIndex: number;
  }>;
  snapshotCreatedAt: string;
}

// ============================================================================
// TEMPLATE REVIEW TYPES
// ============================================================================

export interface TemplateReviewQueueEntry {
  reviewId: string;
  templateId: string;
  templateType: 'PHASE' | 'SESSION';
  versionNumber: number;
  submittedBy: string;
  submittedAt: string;
  organizationId: string;
  targetScope: 'ORGANIZATION';
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'WITHDRAWN';
  reviewedBy?: string;
  reviewedAt?: string;
  reviewNotes?: string;
  submissionNotes?: string;
}

// ============================================================================
// TEMPLATE MANAGEMENT TYPES
// ============================================================================

export interface BrowseTemplatesParams {
  scope?: TemplateScope;
  ownerId?: string;
  category?: TemplateType | PhaseCategory;
  isPrebuilt?: boolean;
  isDefault?: boolean;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface CreatePhaseTemplateInput {
  name: string;
  description: string;
  duration: string;
  purpose: string;
  goal: string;
  successCriteria: string[];
  category: PhaseCategory;
  scope?: TemplateScope;
  ownerId?: string;
  createdBy: string;
}

export interface CreateSessionTemplateInput {
  name: string;
  description: string;
  purpose: string;
  goal: string;
  category: TemplateType;
  scope?: TemplateScope;
  ownerId?: string;
  createdBy: string;
  phaseReferences: PhaseReference[];
  outputDefinitions?: OutputDefinitions;

}

// ============================================================================
// PROJECT TYPES
// ============================================================================

// PROJECT SENSITIVITY LEVELS
export enum ProjectSensitivity {
  NORMAL = 'NORMAL',    // Uses shared AOSS collection per org with metadata filtering
  HIGH = 'HIGH'         // Gets dedicated AOSS collection per project
}

export interface Project {
  projectId: string;
  name: string;
  description: string;
  ownerId: string;
  ownerEmail: string;
  organizationId: string;
  status: 'active' | 'archived';
  sensitivityLevel: ProjectSensitivity;  // Set at creation, cannot be changed
  acl: ProjectACLEntry[];
  sessionCount: number;
  documentCount: number;
  workflowCount: number;
  vectorCollectionName: string;
  vectorCollectionEndpoint: string;
  engagementConfig?: any;
  trainingSandboxEnabled?: boolean;
  lastActivityAt: string;
  createdAt: string;
  updatedAt: string;
  // The caller's access, from GET /api/projects/:id (Cedar). Display only.
  myPermissions?: Array<'Read' | 'Write' | 'Delete' | 'ManageAcl'>;
  viaBreakGlass?: Array<'Read' | 'Write' | 'Delete' | 'ManageAcl'>;
}

export interface ProjectACLEntry {
  userId: string;
  email: string;
  accessLevel: 'read' | 'write' | 'admin';
  grantedAt: string;
  grantedBy: string;
}

export interface CreateProjectInput {
  name: string;
  description: string;
  sensitivityLevel?: ProjectSensitivity; // Optional - defaults to NORMAL
  trainingSandboxEnabled?: boolean; // Optional - defaults to false
}

export interface UpdateProjectInput {
  name?: string;
  description?: string;
  status?: 'active' | 'archived';
  engagementConfig?: any;
}

// Phase 7: Avatar Knowledge Base Documents
export interface AvatarKBDocument {
  documentId: string;
  avatarDefinitionId: string;
  fileName: string;
  fileType: string;
  fileSizeBytes: number;
  s3Key: string;
  uploadedBy: string;
  uploadedAt: string;
  processingStatus: 'pending' | 'processing' | 'completed' | 'failed';
  embeddingStatus: 'pending' | 'processing' | 'completed' | 'failed';
  chunkCount: number;
  vectorIds: string[];
  vectorCollectionName: string;
  organizationId: string;
  description?: string;
  metadata: {
    organizationId: string;
    avatarDefinitionId: string;
    documentType: 'avatar';
    tags?: string[];
  };
}

// ============================================================================
// FACILITATOR CHAT TYPES
// ============================================================================

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  references?: ChatReference[];
}

export interface ChatReference {
  type: 'transcript' | 'guidance' | 'requirement' | 'avatar';
  id: string;
  label: string;
  excerpt?: string;
  scrollToId?: string; // For navigation
}

export type StakeholderGovernanceLevel = 'sponsor' | 'sign_off' | 'consulted' | 'informed';

export interface Stakeholder {
  id: string;
  projectId: string;
  name: string;
  role: string;
  governanceLevel: StakeholderGovernanceLevel;
  areaOfInterest?: string;
  signOffOrder?: number;
  notes?: string;
  createdAt: string;
  createdBy?: string;
  updatedAt?: string;
}

